/* eslint-disable */
import React, {createContext, useContext, useState, useCallback, useEffect} from "react";
import {AuthProvider} from "react-oidc-context";
import {getOidcConfigForProvider, readStoredProvider, storeProvider} from "./identityProviders";
import {hasApplicationAccess} from "./permissions";
import {PROVIDERS} from "src/config/providers";

export const isUserAuthenticated = (auth) => {
  if (!auth?.isAuthenticated) return false;

  const exp = auth?.user?.profile?.exp;
  const isExpired = !exp || exp * 1000 < Date.now();

  return !isExpired && hasApplicationAccess(auth?.user);
};

export const isSignedInWithoutAccess = (auth) => {
  return !!(auth?.isAuthenticated && !hasApplicationAccess(auth.user));
};

export const performLogout = async (auth) => {
  try {
    await auth.signoutRedirect();
  } catch (err) {
    console.warn('[auth] signoutRedirect failed; session was cleared locally only:', err);
  }
};

const LDAAuthContext = createContext();

export const LDAAuthProvider = ({children}) => {
  const getInitialConfig = () => {
    const storedProvider = readStoredProvider();
    if (!storedProvider) {
      storeProvider(PROVIDERS.OTTO_SCHMIDT);
      return getOidcConfigForProvider(PROVIDERS.OTTO_SCHMIDT);
    }
    return getOidcConfigForProvider(storedProvider);
  };

  const [oidcConfig, setOidcConfig] = useState(getInitialConfig);
  const [key, setKey] = useState(0);

  const selectConfig = useCallback((config) => {
    setOidcConfig(config);
    setKey((prev) => prev + 1); 
  }, []);

  const getConfig = useCallback(() => oidcConfig, [oidcConfig]);

  const onSigninCallback = useCallback(() => {
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  // Keep already-open tabs in sync: when another tab logs in or out, the
  // shared (localStorage-backed) OIDC user entry changes, which fires a
  // native `storage` event here. Force AuthProvider to remount so it re-runs
  // its mount-time userManager.getUser() hydration and picks up the change,
  // reusing the same remount mechanism selectConfig() already uses above.
  // Only remount on an actual login/logout transition (oldValue/newValue
  // empty vs present) — automaticSilentRenew also rewrites this key on every
  // token refresh, and remounting on those too would reset in-progress state
  // (e.g. unsaved text in the editor) in every other open tab.
  useEffect(() => {
    const handleStorage = (e) => {
      if (!e.key || !e.key.startsWith("oidc.user:")) return;
      const loggedInElsewhere = !e.oldValue && !!e.newValue;
      const loggedOutElsewhere = !!e.oldValue && !e.newValue;
      if (loggedInElsewhere || loggedOutElsewhere) {
        setKey((prev) => prev + 1);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <LDAAuthContext.Provider value={{selectConfig, getConfig}}>
      {oidcConfig ? (
        <AuthProvider {...oidcConfig} key={key} onSigninCallback={onSigninCallback}>
          {children}
        </AuthProvider>
      ) : (
        children
      )}
    </LDAAuthContext.Provider>
  );
};

export const useLDAAuth = () => useContext(LDAAuthContext);
